import { Controller } from "@hotwired/stimulus"

// The post composer: one ordered list of slides (saved, prefilled, picked from
// the library, or uploaded) plus a live preview of how the post will look.
//
// Replaces four separate mechanisms that used to disagree with each other: a
// read-only "Current images" list that was the only sortable one and only after
// save, a prefilled list, a picker tray, and upload slots. They rendered in
// different styles, in different places, and slide order was whatever order the
// server happened to build them in.
//
// Every slide is a `.slide` row carrying its own form fields. Order is the DOM
// order of that list, written into a position field on every change, so a
// carousel can be arranged before it has ever been saved.
export default class extends Controller {
  static targets = ["list", "empty", "preview", "previewCaption", "count",
                    "typeHint", "addButton", "uploadInput", "slideTemplate"]
  static values  = { handle: String }

  connect() {
    this.dragging = null
    this.renumber()
    this.render()
  }

  // ── Slide list ────────────────────────────────────────────────────────

  get slides() {
    return Array.from(this.listTarget.querySelectorAll(".slide:not(.slide--removed)"))
  }

  get postType() {
    const el = this.element.querySelector('[name="social_media_post[post_type]"]')
    return el ? el.value : "single"
  }

  get platform() {
    const el = this.element.querySelector('[name="social_media_post[platform]"]')
    return el ? el.value : "instagram"
  }

  maxSlides() {
    return this.postType === "carousel" ? 10 : 1
  }

  // Writes the visual order into each slide's position field and relabels.
  // Saved slides post as existing_images[id][position]; new ones as
  // images[key][position] — both honoured server-side.
  renumber() {
    this.slides.forEach((slide, i) => {
      const field = slide.querySelector(".slide-position")
      if (field) field.value = i + 1
      const label = slide.querySelector(".slide-number")
      if (label) label.textContent = i + 1
    })
    this.updateCounts()
  }

  updateCounts() {
    const n   = this.slides.length
    const max = this.maxSlides()

    if (this.hasCountTarget) {
      this.countTarget.textContent = n === 1 ? "1 image" : `${n} images`
    }
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", n > 0)
    }
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.toggle("hidden", n >= max)
    }
    if (this.hasTypeHintTarget) {
      this.typeHintTarget.textContent = this.typeHint(n, max)
    }
  }

  typeHint(n, max) {
    switch (this.postType) {
      case "carousel":  return n < 2 ? `Carousel: add at least 2 (${n}/10)` : `Carousel: ${n} of 10`
      case "story":     return "Story: one image, shown 9:16"
      case "reel":      return "Reel: upload a video; the image is the cover"
      case "video_pin": return "Video pin: upload a video; the image is the cover"
      default:          return n > max ? "Single post takes one image" : "Single post: one image"
    }
  }

  // ── Removing ──────────────────────────────────────────────────────────
  // Saved slides are kept in the DOM but marked, so the remove checkbox the
  // server expects still posts. Unsaved ones are simply dropped.

  removeSlide(event) {
    const slide = event.target.closest(".slide")
    if (!slide) return

    const removeBox = slide.querySelector(".slide-remove-flag")
    if (removeBox) {
      removeBox.checked = true
      slide.classList.add("slide--removed", "hidden")
    } else {
      slide.remove()
    }
    this.renumber()
    this.render()
  }

  // ── Drag to reorder ───────────────────────────────────────────────────

  dragStart(event) {
    this.dragging = event.target.closest(".slide")
    if (!this.dragging) return
    this.dragging.classList.add("opacity-40")
    event.dataTransfer.effectAllowed = "move"
  }

  dragEnd() {
    this.dragging?.classList.remove("opacity-40")
    this.dragging = null
    this.renumber()
    this.render()
  }

  dragOver(event) {
    event.preventDefault()
    if (!this.dragging) return

    const rows = this.slides.filter(s => s !== this.dragging)
    const next = rows.find(row => {
      const box = row.getBoundingClientRect()
      return event.clientY < box.top + box.height / 2
    })
    this.listTarget.insertBefore(this.dragging, next || null)
  }

  // ── Mockup library ────────────────────────────────────────────────────
  // The picker renders filters server-side and fetches result markup; the
  // composer owns fetching, paging and turning a chosen option into a slide.

  toggleLibrary() {
    const panel = this.element.querySelector("#asset-picker")
    if (!panel) return
    panel.classList.toggle("hidden")
    if (!panel.classList.contains("hidden") && !panel.dataset.loaded) this.loadPicker()
  }

  pickerParams(extraUrl) {
    const panel = this.element.querySelector("#asset-picker")
    const url   = new URL(extraUrl || panel.dataset.pickerUrl, window.location.origin)

    panel.querySelectorAll("[data-picker-filter]").forEach(el => {
      const key   = el.dataset.pickerFilter
      const value = el.type === "checkbox" ? (el.checked ? "1" : "") : el.value
      if (value) url.searchParams.set(key, value)
    })
    return url
  }

  async loadPicker(pageUrl = null) {
    const panel   = this.element.querySelector("#asset-picker")
    const results = panel.querySelector("#asset-picker-results")
    results.innerHTML = `<p class="text-sm text-stone-400 text-center py-6">Looking…</p>`

    try {
      const response = await fetch(this.pickerParams(pageUrl), {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      })
      results.innerHTML = await response.text()
      panel.dataset.loaded = "1"
      this.syncPickerChecks()
    } catch {
      results.innerHTML = `<p class="text-sm text-red-500 text-center py-6">Couldn't load mockups — try again.</p>`
    }
  }

  // Delegated: the results markup is replaced on every fetch and page, so
  // binding to individual nodes would go stale.
  pickerClick(event) {
    if (event.target.closest("[data-picker-apply]")) {
      event.preventDefault()
      return this.loadPicker()
    }

    if (event.target.closest("[data-picker-reset]")) {
      event.preventDefault()
      this.element.querySelectorAll("[data-picker-filter]").forEach(el => {
        if (el.type === "checkbox") el.checked = false
        else el.value = ""
      })
      return this.loadPicker()
    }

    const pageLink = event.target.closest("[data-picker-pagination] a")
    if (pageLink) {
      event.preventDefault()
      return this.loadPicker(pageLink.getAttribute("href"))
    }
  }

  pickerChange(event) {
    const box = event.target.closest(".asset-checkbox")
    if (!box) return

    if (box.checked) {
      this.addAsset({
        id:    box.dataset.assetId,
        src:   box.dataset.assetThumb,
        title: box.dataset.assetLabel,
        alt:   box.dataset.assetAlt
      })
      // Adding past the limit is refused; keep the checkbox honest about it.
      box.checked = !!this.listTarget.querySelector(`.slide[data-asset-id="${box.dataset.assetId}"]`)
    } else {
      const slide = this.listTarget.querySelector(`.slide[data-asset-id="${box.dataset.assetId}"]`)
      if (slide) {
        const flag = slide.querySelector(".slide-remove-flag")
        if (flag) { flag.checked = true; slide.classList.add("slide--removed", "hidden") }
        else slide.remove()
      }
      this.refresh()
    }
  }

  // Re-tick options for slides already in the list after a fetch or page turn.
  syncPickerChecks() {
    const chosen = new Set(this.slides.map(s => s.dataset.assetId).filter(Boolean))
    this.element.querySelectorAll(".asset-checkbox").forEach(box => {
      box.checked = chosen.has(box.dataset.assetId)
    })
  }

  // Builds a slide from a picker option's data attributes.
  addAsset(detail) {
    if (this.slides.length >= this.maxSlides()) return
    if (this.listTarget.querySelector(`.slide[data-asset-id="${detail.id}"]`)) return

    const key  = `asset_${detail.id}`
    const html = `
      <input type="hidden" name="images[${key}][type]" value="generated_mockup">
      <input type="hidden" name="images[${key}][generated_asset_id]" value="${detail.id}">
      <input type="hidden" name="images[${key}][position]" class="slide-position" value="">
    `
    this.appendSlide({
      assetId: detail.id,
      src:     detail.src,
      title:   detail.title,
      altName: `images[${key}][alt_text]`,
      alt:     detail.alt || "",
      fields:  html
    })
  }

  // ── Uploading ─────────────────────────────────────────────────────────

  addUpload() { this.uploadInputTarget.click() }

  uploadChosen(event) {
    const files = Array.from(event.target.files || [])
    const room  = this.maxSlides() - this.slides.length

    files.slice(0, Math.max(room, 0)).forEach((file, i) => {
      const key    = `upload_${Date.now()}_${i}`
      const reader = new FileReader()

      // A DataTransfer lets each upload own its own file input, so slides stay
      // independently removable and reorderable.
      const dt = new DataTransfer()
      dt.items.add(file)

      reader.onload = () => {
        const slide = this.appendSlide({
          src:     reader.result,
          title:   file.name,
          altName: `images[${key}][alt_text]`,
          alt:     "",
          fields: `
            <input type="hidden" name="images[${key}][type]" value="custom">
            <input type="hidden" name="images[${key}][position]" class="slide-position" value="">
          `
        })
        const input = document.createElement("input")
        input.type  = "file"
        input.name  = `images[${key}][file]`
        input.className = "hidden"
        input.files = dt.files
        slide.appendChild(input)
        this.render()
      }
      reader.readAsDataURL(file)
    })

    event.target.value = ""
  }

  appendSlide({ assetId = "", src, title, altName, alt, fields }) {
    const node = this.slideTemplateTarget.content.firstElementChild.cloneNode(true)
    if (assetId) node.dataset.assetId = assetId

    node.querySelector(".slide-thumb").src = src
    node.querySelector(".slide-title").textContent = title || "Image"

    const altInput = node.querySelector(".slide-alt")
    altInput.name  = altName
    altInput.value = alt

    node.insertAdjacentHTML("beforeend", fields)
    this.listTarget.appendChild(node)
    this.renumber()
    this.render()
    return node
  }

  // ── Live preview ──────────────────────────────────────────────────────

  render() {
    if (!this.hasPreviewTarget) return

    const srcs = this.slides.map(s => s.querySelector(".slide-thumb")?.src).filter(Boolean)
    const shape = this.previewShape()

    this.previewTarget.innerHTML = srcs.length
      ? this.previewMarkup(srcs, shape)
      : `<div class="aspect-square bg-stone-100 rounded-lg flex items-center justify-center
                     text-sm text-stone-400">Add an image to preview</div>`

    if (this.hasPreviewCaptionTarget) {
      const field = this.element.querySelector('[name="social_media_post[caption]"]')
      const text  = (field?.value || "").trim()
      this.previewCaptionTarget.textContent = text || "Your caption will appear here."
      this.previewCaptionTarget.classList.toggle("text-stone-300", !text)
    }
  }

  // Aspect the platform will actually show, so the crop is honest.
  previewShape() {
    if (this.postType === "story" || this.postType === "reel") return "aspect-[9/16]"
    if (this.platform === "pinterest") return "aspect-[2/3]"
    return "aspect-[4/5]"
  }

  previewMarkup(srcs, shape) {
    const first = `<img src="${srcs[0]}" class="w-full h-full object-cover">`
    if (srcs.length === 1) {
      return `<div class="${shape} rounded-lg overflow-hidden bg-stone-100">${first}</div>`
    }

    // Carousel: the first slide with a dot row, matching what a viewer sees.
    const dots = srcs.map((_, i) =>
      `<span class="w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-mauve-600' : 'bg-stone-300'}"></span>`
    ).join("")

    return `
      <div class="${shape} rounded-lg overflow-hidden bg-stone-100 relative">
        ${first}
        <div class="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
          1/${srcs.length}
        </div>
      </div>
      <div class="flex justify-center gap-1 mt-2">${dots}</div>`
  }

  // Re-render on any input that changes how the post reads.
  refresh() {
    this.renumber()
    this.render()
  }
}
